import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserData, refreshUserProfile } from './dashboardService';
import { auth } from '../api/api';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

const ALL_MENU_ITEMS = [
  { id: 'home', label: 'Home', icon: 'home', roles: ['admin', 'user'] },
  { id: 'mymodule', label: 'MyModule', icon: 'book', roles: ['user'] },
  { id: 'assignment', label: 'Assignment', icon: 'clipboard', roles: ['user'] },
  { id: 'certification', label: 'Certification', icon: 'award', roles: ['user'] },
  { id: 'usermanagement', label: 'User Management', icon: 'users', roles: ['admin'] },
  { id: 'modulemanagement', label: 'Module Management', icon: 'folder', roles: ['admin'] },
  { id: 'helpcentre', label: 'Help Centre', icon: 'help-circle', roles: ['admin', 'user'] }
];

const getMenuItemsForRole = (role) => {
  const r = (role || '').toString().toLowerCase();
  const isAdmin = r === 'admin' || r === 'administrator';
  return ALL_MENU_ITEMS.filter((item) => item.roles.includes(isAdmin ? 'admin' : 'user'));
};

const getIcon = (iconName) => {
  const icons = {
    home: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
    ),
    book: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
      </svg>
    ),
    award: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="8" r="7"></circle>
        <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
      </svg>
    ),
    users: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    ),
    folder: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
      </svg>
    ),
    'help-circle': (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
    ),
    clipboard: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
      </svg>
    )
  };
  return icons[iconName] || icons.home;
};

const DashboardShell = ({ activeMenuId = 'home', children }) => {
  const { setUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadUser = async () => {
      const data = getUserData();
      if (!data) {
        navigate('/login');
        return;
      }
      if (data.access_token) {
        const refreshed = await refreshUserProfile();
        setUserData(refreshed);
      } else {
        setUserData(data);
      }
    };
    loadUser();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch (_) {
      // ignore
    }
    setUser(null);
    navigate('/login');
  };

  const handleMenuClick = (menuId) => {
    const currentParams = new URLSearchParams(location.search);
    const currentTab = currentParams.get('tab');

    const nextParams = new URLSearchParams();
    nextParams.set('menu', menuId);
    if (menuId === 'mymodule' && currentTab) nextParams.set('tab', currentTab);

    navigate(`/dashboard?${nextParams.toString()}`);
  };

  if (!userData) return null;

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo-section">
            <img src={process.env.PUBLIC_URL + '/BUNI.png'} alt="BUNI" className="dashboard-logo-img" />
            <span className="dashboard-logo-lms">LMS</span>
          </div>
          <div className="user-section">
            <div className="user-profile" onClick={() => setShowUserDropdown(!showUserDropdown)}>
              <div className="user-avatar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <div className="user-info">
                <div className="user-name">{userData.name || userData.email}</div>
                <div className="user-role">{userData.role === 'Admin' ? 'Administrator' : 'User'}</div>
              </div>
              <svg className="dropdown-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            {showUserDropdown && (
              <div className="user-dropdown">
                <button className="dropdown-item" onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="dashboard-body">
        <aside className="dashboard-sidebar">
          <nav className="sidebar-nav">
            {getMenuItemsForRole(userData?.role).map((item) => (
              <button
                key={item.id}
                className={`nav-item ${activeMenuId === item.id ? 'active' : ''}`}
                onClick={() => handleMenuClick(item.id)}
              >
                <span className="nav-icon">{getIcon(item.icon)}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="dashboard-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardShell;

